/**
 * Painel da Loja — fila, transicoes e balcao (`API §6.19`, ADR-0013).
 *
 * ## O `If-Match` das transicoes, sem a tela saber
 *
 * `API §4.1` da a razao numa frase: *"Dois balconistas no mesmo tablet/PC do PDV. E o caso mais
 * frequente"*. Toda transicao exige `If-Match` (`428 PRECONDITION_REQUIRED` sem ele), e o
 * cliente o monta sozinho:
 *
 * ```text
 *  GET /v1/store/orders  -> cada item traz `version` -> semeia `order:<id>` no cofre de versoes
 *  POST .../accept       -> manda If-Match: W/"order-<id>-<version>"
 *  resposta              -> traz ETag novo -> o cofre atualiza
 * ```
 *
 * Semear a partir da fila e o que evita um `GET` por pedido antes de cada aceite — a fila do
 * polling de 5 s (ADR-0013) ja carrega a versao de todos.
 *
 * ## Idempotencia
 *
 * As oito transicoes estao na tabela do `API §3.1` ("Transicao repetida por duplo toque em
 * tablet com rede ruim"), e `POST /v1/store/orders` tambem (pedido assistido, D9). Todas levam
 * chave com escopo no caminho — que ja carrega o id do pedido.
 *
 * ## Uma ausencia que e contrato
 *
 * Nenhuma requisicao daqui tem campo de preco (`store/counter.ts`: *"o balcao usa o mesmo motor
 * de preco do cliente; um campo de dinheiro na entrada seria o caminho alternativo que o
 * `CONTRACTS §8.3` proibe"*).
 */
import {
  ApplyAppPriceResponseSchema,
  CashClosingResponseSchema,
  DispatchOrderResponseSchema,
  MarkReadyResponseSchema,
  OrderCreatedResponseSchema,
  SettlePaymentResponseSchema,
  ShortageResponseSchema,
  StorePauseStateSchema,
  StoreQueueResponseSchema,
  StoreOrderDetailSchema,
  StoreSummaryResponseSchema,
  StoreTicketSchema,
  ValidatePickupResponseSchema,
  orderWeakETag,
  type ApplyAppPriceRequest,
  type ApplyAppPriceResponse,
  type CashClosingRequest,
  type CashClosingResponse,
  type CreateAssistedOrderRequest,
  type DeliverOrderRequest,
  type DispatchOrderRequest,
  type DispatchOrderResponse,
  type MarkReadyRequest,
  type MarkReadyResponse,
  type OrderCreatedResponse,
  type PickOrderItemRequest,
  type RejectOrderRequest,
  type SettlePaymentRequest,
  type SettlePaymentResponse,
  type ShortageRequest,
  type ShortageResponse,
  type StorePauseRequest,
  type StorePauseState,
  type StoreQueueQuerySchema,
  type StoreQueueResponse,
  type StoreOrderDetail,
  type StoreResumeRequest,
  type StoreSummaryQuerySchema,
  type StoreSummaryResponse,
  type StoreTicket,
  type ValidatePickupRequest,
  type ValidatePickupResponse,
} from '@farmacia/contracts';
import type { z } from 'zod';

import type { ApiClientCore } from '../core.js';
import type { ApiRequestSpec } from '../http.js';
import { orderVersionKey } from '../versions.js';

export type StoreQueueInput = z.input<typeof StoreQueueQuerySchema>;
export type StoreSummaryInput = z.input<typeof StoreSummaryQuerySchema>;

export interface StorePanelApi {
  queue: (query: StoreQueueInput, signal?: AbortSignal) => Promise<StoreQueueResponse>;
  accept: (orderId: string) => Promise<StoreOrderDetail>;
  reject: (orderId: string, body: RejectOrderRequest) => Promise<StoreOrderDetail>;
  pickItem: (
    orderId: string,
    itemId: string,
    body: PickOrderItemRequest,
  ) => Promise<StoreOrderDetail>;
  reportShortage: (
    orderId: string,
    itemId: string,
    body: ShortageRequest,
  ) => Promise<ShortageResponse>;
  markReady: (orderId: string, body: MarkReadyRequest) => Promise<MarkReadyResponse>;
  dispatch: (orderId: string, body: DispatchOrderRequest) => Promise<DispatchOrderResponse>;
  deliver: (orderId: string, body: DeliverOrderRequest) => Promise<StoreOrderDetail>;
  /** `POST /v1/store/orders/{id}/print` — o ticket de 80 mm. */
  printTicket: (orderId: string) => Promise<StoreTicket>;

  /** Pedido assistido (D9) — `POST /v1/store/orders`. */
  createAssistedOrder: (body: CreateAssistedOrderRequest) => Promise<OrderCreatedResponse>;
  applyAppPrice: (orderId: string, body: ApplyAppPriceRequest) => Promise<ApplyAppPriceResponse>;
  settlePayment: (orderId: string, body: SettlePaymentRequest) => Promise<SettlePaymentResponse>;

  pause: (body: StorePauseRequest) => Promise<StorePauseState>;
  resume: (body: StoreResumeRequest) => Promise<StorePauseState>;
  validatePickup: (body: ValidatePickupRequest) => Promise<ValidatePickupResponse>;
  closeCash: (body: CashClosingRequest) => Promise<CashClosingResponse>;
  summary: (query: StoreSummaryInput, signal?: AbortSignal) => Promise<StoreSummaryResponse>;
}

export function createStorePanelApi(core: ApiClientCore): StorePanelApi {
  /**
   * Uma transicao: `If-Match` da versao guardada, chave de idempotencia, e o `ETag` de volta.
   *
   * `readVersion` existe porque as respostas nao tem uma forma so — `accept` devolve o pedido,
   * `shortage` devolve `{ order, ... }`. O `ETag` do header ja bastaria, mas semear tambem pelo
   * corpo cobre o caso do proxy que remove `ETag` de resposta de `POST`.
   */
  async function transition<TResponse>(
    orderId: string,
    spec: Omit<ApiRequestSpec<TResponse>, 'ifMatch' | 'idempotency'>,
    readVersion: (data: TResponse) => number | null,
  ): Promise<TResponse> {
    const key = orderVersionKey(orderId);
    const response = await core.request({
      ...spec,
      ifMatch: core.versions.current(key),
      idempotency: true,
    });

    if (response.etag !== null) {
      core.versions.remember(key, response.etag);
    } else {
      const version = readVersion(response.data);
      core.versions.remember(key, version === null ? null : orderWeakETag(orderId, version));
    }

    return response.data;
  }

  return {
    async queue(query, signal) {
      const { data } = await core.request({
        method: 'GET',
        path: '/v1/store/orders',
        query: { ...query },
        response: StoreQueueResponseSchema,
        ...(signal === undefined ? {} : { signal }),
      });

      // Semeia a versao de cada pedido da fila. Sem isto, a primeira transicao de cada pedido
      // precisaria de um `GET` proprio — 20 pedidos na fila seriam 20 requisicoes a mais por
      // turno, num painel que ja faz polling a cada 5 s.
      for (const item of data.data) {
        core.versions.remember(orderVersionKey(item.id), orderWeakETag(item.id, item.version));
      }
      // O delta tambem diz quem SAIU. Guardar a versao de um pedido que ja e terminal so
      // acumula memoria e mascara um `404` como conflito.
      for (const removedId of data.removed) core.versions.forget(orderVersionKey(removedId));

      return data;
    },

    accept(orderId) {
      return transition(
        orderId,
        {
          method: 'POST',
          path: `/v1/store/orders/${encodeURIComponent(orderId)}/accept`,
          body: {},
          response: StoreOrderDetailSchema,
        },
        (order) => order.version,
      );
    },

    reject(orderId, body) {
      return transition(
        orderId,
        {
          method: 'POST',
          path: `/v1/store/orders/${encodeURIComponent(orderId)}/reject`,
          body,
          response: StoreOrderDetailSchema,
        },
        (order) => order.version,
      );
    },

    pickItem(orderId, itemId, body) {
      return transition(
        orderId,
        {
          method: 'POST',
          path: `/v1/store/orders/${encodeURIComponent(orderId)}/items/${encodeURIComponent(itemId)}/pick`,
          body,
          response: StoreOrderDetailSchema,
        },
        (order) => order.version,
      );
    },

    reportShortage(orderId, itemId, body) {
      return transition(
        orderId,
        {
          method: 'POST',
          path: `/v1/store/orders/${encodeURIComponent(orderId)}/items/${encodeURIComponent(itemId)}/shortage`,
          body,
          response: ShortageResponseSchema,
        },
        (result) => result.order.version,
      );
    },

    markReady(orderId, body) {
      return transition(
        orderId,
        {
          method: 'POST',
          path: `/v1/store/orders/${encodeURIComponent(orderId)}/ready`,
          body,
          response: MarkReadyResponseSchema,
        },
        (result) => result.order.version,
      );
    },

    dispatch(orderId, body) {
      return transition(
        orderId,
        {
          method: 'POST',
          path: `/v1/store/orders/${encodeURIComponent(orderId)}/dispatch`,
          body,
          response: DispatchOrderResponseSchema,
        },
        (result) => result.order.version,
      );
    },

    deliver(orderId, body) {
      return transition(
        orderId,
        {
          method: 'POST',
          path: `/v1/store/orders/${encodeURIComponent(orderId)}/deliver`,
          body,
          response: StoreOrderDetailSchema,
        },
        (order) => order.version,
      );
    },

    async printTicket(orderId) {
      // Imprimir nao move o pedido: sem `If-Match`, sem chave de idempotencia. Reimprimir e
      // uma operacao legitima do balcao — o papel amassa.
      const { data } = await core.request({
        method: 'POST',
        path: `/v1/store/orders/${encodeURIComponent(orderId)}/print`,
        body: {},
        response: StoreTicketSchema,
      });
      return data;
    },

    async createAssistedOrder(body) {
      const { data } = await core.request({
        method: 'POST',
        path: '/v1/store/orders',
        body,
        response: OrderCreatedResponseSchema,
        idempotency: true,
      });
      return data;
    },

    applyAppPrice(orderId, body) {
      // Honrar o preco do app **e** uma transicao sob `If-Match`: o `counter-orders.controller`
      // exige a pre-condicao e devolve `ETag` com a versao nova. A resposta traz `version`
      // solto (nao dentro de `order`), e e dele que o cofre se realimenta se o `ETag` faltar.
      return transition(
        orderId,
        {
          method: 'POST',
          path: `/v1/store/orders/${encodeURIComponent(orderId)}/apply-app-price`,
          body,
          response: ApplyAppPriceResponseSchema,
        },
        (result) => result.version,
      );
    },

    async settlePayment(orderId, body) {
      // A baixa do pagamento na entrega **nao** e transicao de estado do pedido: o
      // `counter-orders.controller` nao exige `If-Match` e nao devolve `ETag`. Mandar
      // `If-Match` aqui seria pre-condicao que ninguem verifica, e realimentar o cofre com uma
      // versao que nao veio produziria conflito falso na proxima transicao de verdade.
      // A idempotencia continua: dois `PAGO` no mesmo pedido atrasam o fechamento de caixa.
      const { data } = await core.request({
        method: 'POST',
        path: `/v1/store/orders/${encodeURIComponent(orderId)}/payments/settle`,
        body,
        response: SettlePaymentResponseSchema,
        idempotency: true,
      });
      return data;
    },

    async pause(body) {
      const { data } = await core.request({
        method: 'POST',
        path: '/v1/store/pause',
        body,
        response: StorePauseStateSchema,
      });
      return data;
    },

    async resume(body) {
      const { data } = await core.request({
        method: 'POST',
        path: '/v1/store/resume',
        body,
        response: StorePauseStateSchema,
      });
      return data;
    },

    async validatePickup(body) {
      const { data } = await core.request({
        method: 'POST',
        path: '/v1/store/pickups/validate',
        body,
        response: ValidatePickupResponseSchema,
      });
      return data;
    },

    async closeCash(body) {
      const { data } = await core.request({
        method: 'POST',
        path: '/v1/store/cash-closing',
        body,
        response: CashClosingResponseSchema,
      });
      return data;
    },

    async summary(query, signal) {
      const { data } = await core.request({
        method: 'GET',
        path: '/v1/store/summary',
        query: { ...query },
        response: StoreSummaryResponseSchema,
        ...(signal === undefined ? {} : { signal }),
      });
      return data;
    },
  };
}
